import { useState, type ReactNode } from "react";
import { useAnchored } from "../lib/anchored";

/**
 * One button that opens a list, instead of a row of loose buttons.
 *
 * Every page used to lay its actions out flat -- export, print, refresh, and
 * on an exception row a pair of link-buttons each -- so the controls competed
 * with the data for the eye. A closed menu is one control; the choices only
 * exist once somebody has decided they want to act.
 *
 * ⚠️ **The popup is `position: fixed`, and that is not a detail.** It used to
 * be absolutely positioned inside the trigger, which meant any ancestor with
 * `overflow: hidden` clipped it -- and the sidebar has exactly that, so the
 * account menu at the bottom of the nav was sliced off at the sidebar edge and
 * ran off the screen. Rather than hunt for which ancestor was doing the
 * clipping and unset it (and then do that again for the next one), the popup
 * is taken out of the clipping context altogether: nothing above it in the
 * tree can crop something positioned against the viewport.
 *
 * ⚠️ THAT LOGIC MOVED TO `lib/anchored.ts` ON 2026-09-07 and this component is
 * now one of its callers rather than its owner. It was the only thing in the
 * app that knew how to open a panel without moving the page, so the three
 * places that needed a confirm grew in-flow expansions instead and every one
 * of them shoved the page down. The hook's docstring carries the rule.
 *
 * Closes on outside click, on Escape, and on choosing something. Escape
 * returns focus to the trigger, so a keyboard user is not dropped at the top
 * of the document.
 */

export default function Menu({
  label,
  children,
  align = "right",
  icon = false,
  disabled = false,
  onDark = false,
  trigger: triggerContent,
}: {
  label: string;
  children: ReactNode;
  /** Which edge of the trigger the menu lines up with, space permitting. */
  align?: "left" | "right";
  /** Render as a compact "···" button, for use inside a dense row. */
  icon?: boolean;
  disabled?: boolean;
  /** Sitting on the navy shell rather than on paper. */
  onDark?: boolean;
  /**
   * Hang the menu off something that is already on screen -- the account
   * avatar, say -- instead of adding a control beside it. The button keeps its
   * role and label; only the thing you click changes.
   */
  trigger?: ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const { anchor, panel, style } = useAnchored<HTMLButtonElement>(
    open,
    () => setOpen(false),
    align,
  );

  return (
    <div className="menu no-print">
      <button
        ref={anchor}
        type="button"
        className={`menu-trigger ${icon ? "icon" : ""} ${onDark ? "on-dark" : ""} ${
          triggerContent ? "plain" : ""
        }`}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={icon || triggerContent ? label : undefined}
        disabled={disabled}
        onClick={() => setOpen((was) => !was)}
      >
        {triggerContent ?? (icon ? "···" : label)}
      </button>

      {open ? (
        // Anything chosen closes the menu. Catching it here rather than
        // wrapping every child means a caller cannot forget to close it.
        <div
          ref={panel}
          className="menu-pop"
          role="menu"
          style={style}
          onClick={(event) => {
            const target = event.target as HTMLElement;
            if (target.closest("button, a")) setOpen(false);
          }}
        >
          {children}
        </div>
      ) : null}
    </div>
  );
}

/** A rule between groups of choices. */
export function MenuSep() {
  return <div className="menu-sep" role="separator" />;
}

/**
 * A label and a value, on one line.
 *
 * This replaced the paragraph that used to sit under every menu choice.
 * Jonathan, on seeing them: *"the words are still a lot ... we don't need
 * those."* He is right -- a menu is a place to act, and a sentence explaining
 * what a button does belongs on the button, as its title, not stacked
 * underneath it in prose. What survives is the half that was a FACT rather
 * than an explanation: when it was last read, how many items are used.
 */
export function MenuFact({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}) {
  return (
    <div className="menu-fact">
      <span className="k">{label}</span>
      <span className="v">{children}</span>
    </div>
  );
}

/** One short line, where a choice genuinely needs one. Never a paragraph. */
export function MenuNote({ children }: { children: ReactNode }) {
  return <div className="menu-note">{children}</div>;
}
